import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { SendWebSocketMessage } from './websocket.actions';
import {
  GameStarted,
  GameStateUpdated,
  GameOver,
  MakeMove,
  ResignGame,
  ClaimDrawGame,
  OfferDrawGame,
  AcceptDrawGame,
  DeclineDrawGame,
  DrawOfferDeclinedReceived,
  InvalidMoveReceived,
  LeaveGame,
  MovesHistoryReceived,
  SelectMovePly,
  ObservationStarted,
  ObserverJoined,
  ObserverLeft,
  GameChatReceived,
  EndDemonstrationGame,
} from './game.actions';
import { Chess, Square } from 'chess.js';

export interface MoveEntry {
  ply: number;
  san: string;
  uci: string;
}

export interface GameChatEntry {
  message_id: string;
  username: string;
  message: string;
  timestamp: string;
  is_observer: boolean;
  kind?: 'chat' | 'system';
}

export interface PlayerSeat {
  seat: number;
  username: string;
}

export interface ActiveGame {
  gameId: string;
  game: string;
  players: PlayerSeat[];
  yourSeat: number | null;
  state: unknown;
  clockMsBySeat: number[];
  currentSeat: number | null;
  lastSyncAt: number;
  initialTimeSecs: number;
  incrementSecs: number;
  rated: boolean;
  result: string | null;
  invalidMoveReason: string | null;
  moves: MoveEntry[];
  currentPly: number;
  mode: 'play' | 'observe' | 'demonstrate' | 'replay';
  observers: { username: string; is_bot: boolean }[];
  chat: GameChatEntry[];
  demoTitle?: string;
}

export interface GameStateModel {
  activeGame: ActiveGame | null;
}

export function getFenAtPly(moves: MoveEntry[], ply: number): string {
  const chess = new Chess();
  for (const move of moves.slice(0, ply)) {
    const from = move.uci.substring(0, 2) as Square;
    const to = move.uci.substring(2, 4) as Square;
    const promotion = move.uci.length > 4 ? move.uci[4] : undefined;
    chess.move({ from, to, promotion });
  }
  return chess.fen();
}

@State<GameStateModel>({
  name: 'game',
  defaults: {
    activeGame: null,
  },
})
@Injectable()
export class GameState {
  private router = inject(Router);
  private translate = inject(TranslateService);

  @Selector()
  static activeGame(state: GameStateModel) {
    return state.activeGame;
  }

  @Selector()
  static isInGame(state: GameStateModel) {
    return state.activeGame !== null && state.activeGame.result === null;
  }

  @Selector()
  static isObserving(state: GameStateModel) {
    return state.activeGame !== null && state.activeGame.mode === 'observe';
  }

  @Selector()
  static isDemonstrating(state: GameStateModel) {
    return state.activeGame !== null && state.activeGame.mode === 'demonstrate';
  }

  @Action(GameStarted)
  onGameStarted(ctx: StateContext<GameStateModel>, action: GameStarted) {
    const mode = action.mode === 'demonstration' ? 'demonstrate' : 'play';
    const clockMsBySeat = action.players.map(
      () => action.initialTimeSecs * 1000,
    );
    ctx.patchState({
      activeGame: {
        gameId: action.gameId,
        game: action.game,
        players: action.players,
        yourSeat: action.yourSeat,
        state: action.initialState,
        clockMsBySeat,
        currentSeat: action.players.length > 0 ? 0 : null,
        lastSyncAt: Date.now(),
        initialTimeSecs: action.initialTimeSecs,
        incrementSecs: action.incrementSecs,
        rated: action.rated ?? false,
        result: null,
        invalidMoveReason: null,
        moves: [],
        currentPly: 0,
        mode,
        observers: [],
        chat: [],
      },
    });
    if (action.game === 'chess') {
      ctx.dispatch(
        new SendWebSocketMessage({
          type: 'get_moves_history',
          payload: { game_id: action.gameId },
        }),
      );
    }
    this.router.navigate(['/game', action.gameId]);
  }

  @Action(GameStateUpdated)
  onStateUpdated(ctx: StateContext<GameStateModel>, action: GameStateUpdated) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;

    const state = action.state as Record<string, unknown>;
    let moves = game.moves;
    let currentPly = game.currentPly;

    if (game.game === 'chess' && state) {
      const serverPly = (state['current_ply'] as number) || 0;

      if (serverPly > 0 && game.moves.length === 0) {
        ctx.dispatch(
          new SendWebSocketMessage({
            type: 'get_moves_history',
            payload: { game_id: action.gameId },
          }),
        );
      }

      const lastSan = state['last_move_san'] as string | null | undefined;
      const lastUci = state['last_move_uci'] as string | null | undefined;

      if (lastSan && lastUci && serverPly > 0) {
        const already = moves.some((m) => m.ply === serverPly);
        if (!already) {
          moves = [...moves, { ply: serverPly, san: lastSan, uci: lastUci }];
        }
      }
      currentPly = serverPly;
    }

    ctx.patchState({
      activeGame: {
        ...game,
        state: action.state,
        clockMsBySeat: action.clocksRemainingMs,
        currentSeat: action.currentSeat,
        lastSyncAt: Date.now(),
        invalidMoveReason: null,
        moves,
        currentPly,
      },
    });
  }

  @Action(GameOver)
  onGameOver(ctx: StateContext<GameStateModel>, action: GameOver) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    ctx.patchState({
      activeGame: { ...game, result: action.result },
    });
  }

  @Action(MakeMove)
  makeMove(ctx: StateContext<GameStateModel>, action: MakeMove) {
    const payload: Record<string, unknown> = { game_id: action.gameId, move_data: action.moveData };
    if (typeof action.basePly === 'number') {
      payload['base_ply'] = action.basePly;
    }
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'game_move',
        payload,
      }),
    );
  }

  @Action(ResignGame)
  resignGame(ctx: StateContext<GameStateModel>, action: ResignGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'resign_game',
        payload: { game_id: action.gameId },
      }),
    );
  }

  @Action(ClaimDrawGame)
  claimDraw(ctx: StateContext<GameStateModel>, action: ClaimDrawGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'claim_draw',
        payload: { game_id: action.gameId },
      }),
    );
  }

  @Action(OfferDrawGame)
  offerDraw(ctx: StateContext<GameStateModel>, action: OfferDrawGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'offer_draw',
        payload: { game_id: action.gameId },
      }),
    );
  }

  @Action(AcceptDrawGame)
  acceptDraw(ctx: StateContext<GameStateModel>, action: AcceptDrawGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'accept_draw',
        payload: { game_id: action.gameId },
      }),
    );
  }

  @Action(DeclineDrawGame)
  declineDraw(ctx: StateContext<GameStateModel>, action: DeclineDrawGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'decline_draw',
        payload: { game_id: action.gameId },
      }),
    );
  }

  @Action(DrawOfferDeclinedReceived)
  onDrawOfferDeclined(ctx: StateContext<GameStateModel>, _action: DrawOfferDeclinedReceived) {
    const game = ctx.getState().activeGame;
    if (!game) return;
    const otherPlayer = game.players.find((p) => p.seat !== game.yourSeat);
    const opponent = otherPlayer?.username ?? 'Opponent';
    const entry: GameChatEntry = {
      message_id: crypto.randomUUID(),
      username: 'System',
      message: this.translate.instant('GAME.DRAW_DECLINED', { opponent }),
      timestamp: new Date().toISOString(),
      is_observer: false,
      kind: 'system',
    };
    ctx.patchState({
      activeGame: {
        ...game,
        chat: [...game.chat, entry],
      },
    });
  }

  @Action(InvalidMoveReceived)
  onInvalidMove(ctx: StateContext<GameStateModel>, action: InvalidMoveReceived) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    ctx.patchState({
      activeGame: { ...game, invalidMoveReason: action.reason },
    });
  }

  @Action(MovesHistoryReceived)
  onMovesHistory(ctx: StateContext<GameStateModel>, action: MovesHistoryReceived) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    const currentPly = action.moves.length > 0 ? action.moves[action.moves.length - 1].ply : 0;
    ctx.patchState({
      activeGame: {
        ...game,
        moves: action.moves,
        currentPly,
      },
    });
  }

  @Action(SelectMovePly)
  onSelectMovePly(ctx: StateContext<GameStateModel>, action: SelectMovePly) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    ctx.patchState({
      activeGame: { ...game, currentPly: action.ply },
    });
  }

  @Action(LeaveGame)
  leaveGame(ctx: StateContext<GameStateModel>) {
    ctx.patchState({ activeGame: null });
    this.router.navigate(['/lobby']);
  }

  @Action(EndDemonstrationGame)
  endDemonstration(ctx: StateContext<GameStateModel>, action: EndDemonstrationGame) {
    ctx.dispatch(
      new SendWebSocketMessage({
        type: 'end_demonstration',
        payload: {
          game_id: action.gameId,
          save: action.save,
          title: action.title ?? null,
          description: action.description ?? null,
        },
      }),
    );
  }

  @Action(ObservationStarted)
  onObservationStarted(ctx: StateContext<GameStateModel>, action: ObservationStarted) {
    ctx.patchState({
      activeGame: {
        gameId: action.gameId,
        game: action.game,
        players: action.players,
        yourSeat: null,
        state: action.currentState,
        clockMsBySeat: action.clocksRemainingMs,
        currentSeat: action.currentSeat,
        lastSyncAt: Date.now(),
        initialTimeSecs: action.initialTimeSecs,
        incrementSecs: action.incrementSecs,
        rated: action.rated,
        result: null,
        invalidMoveReason: null,
        moves: action.moves,
        currentPly: action.currentPly,
        mode: (action.mode === 'replay' ? 'replay' : 'observe') as 'play' | 'observe' | 'demonstrate' | 'replay',
        observers: action.observers,
        chat: action.chat,
      },
    });
    const qp: Record<string, string> = { observe: '1' };
    if (action.mode === 'replay') qp['replay'] = '1';
    this.router.navigate(['/game', action.gameId], { queryParams: qp });
  }

  @Action(ObserverJoined)
  onObserverJoined(ctx: StateContext<GameStateModel>, action: ObserverJoined) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    ctx.patchState({
      activeGame: {
        ...game,
        observers: [...game.observers, action.observer],
      },
    });
  }

  @Action(ObserverLeft)
  onObserverLeft(ctx: StateContext<GameStateModel>, action: ObserverLeft) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    ctx.patchState({
      activeGame: {
        ...game,
        observers: game.observers.filter((o) => o.username !== action.username),
      },
    });
  }

  @Action(GameChatReceived)
  onGameChatReceived(ctx: StateContext<GameStateModel>, action: GameChatReceived) {
    const game = ctx.getState().activeGame;
    if (!game || game.gameId !== action.gameId) return;
    ctx.patchState({
      activeGame: {
        ...game,
        chat: [
          ...game.chat,
          {
            message_id: action.messageId,
            username: action.username,
            message: action.message,
            timestamp: action.timestamp,
            is_observer: action.isObserver,
            kind: 'chat',
          },
        ],
      },
    });
  }
}
